import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartKanban,
  faCheck,
  faCircle,
  faList,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { useWebSocket } from "@/context/WebSocketContext";
import { WS_CHANNELS } from "@/utils/websocket";
import {
  KanbanBoard,
  type KanbanColumnData,
  type KanbanCardData,
} from "@/components/kanban";
import ButtonGroup, {
  type ButtonGroupOption,
} from "@/components/ui/button-group";
import type { PodTemplate } from "@/services/api/agentic/types";
import {
  podCurrentStatusToDisplayString,
  podCurrentStatusToMachineString,
} from "@/utils/podStatus";
import {
  workflowTaskPrimaryStatus,
  workflowTaskStatusToColumnId,
} from "@/utils/workflowTaskKanban";

interface TaskAction {
  type: string;
  label: string;
  enabled: boolean;
}

interface TaskStatus {
  current_status: string;
  current_stage?: string;
  blocked_reason?: string | null;
  progress_percentage?: number;
  call_to_action?: string;
}

interface TaskAssignee {
  id: string;
  type: string;
  name: string;
}

interface TaskTimeline {
  scheduled_start?: string;
  actual_start?: string | null;
  scheduled_end?: string;
  actual_end?: string | null;
  deadline?: string | null;
}

interface TaskMetadata {
  template_name?: string;
  [key: string]: any; // For any other metadata fields
}

interface TaskDependency {
  task_id: string;
  dependency_type: string;
}

export interface Task {
  _id: string;
  name: string;
  description: string;
  pod_instance_id: string;
  assignee?: TaskAssignee;
  /** API may return a plain status string or a structured `TaskStatus` object. */
  status: TaskStatus | string;
  dependencies?: TaskDependency[];
  timeline?: TaskTimeline;
  interaction_ids?: string[];
  metadata?: TaskMetadata;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  notes?: string;
  progress_percentage?: number;
  current_state?: string;
}

interface TasksProps {
  entityId: string;
  entityType: string;
  fetchTasks: (entityId: string) => Promise<Task[]>;
  podTemplate?: PodTemplate;
}

type ViewMode = "list" | "kanban";

function taskStatusRecord(task: Task): TaskStatus | null {
  const s = task.status;
  if (s && typeof s === "object" && !Array.isArray(s) && "current_status" in s) {
    return s as TaskStatus;
  }
  return null;
}

function taskProgressForCard(task: Task): number | undefined {
  const nested = taskStatusRecord(task)?.progress_percentage;
  if (typeof nested === "number") return nested;
  return task.progress_percentage;
}

/**
 * Maps a task to a Kanban card. When the pod template defines status columns, column ids are
 * those labels (e.g. "Completed"). Without a template, columns use fixed ids from
 * {@link workflowTaskStatusToColumnId} (e.g. "done", "todo").
 */
const mapTaskToKanbanCard = (
  task: Task,
  useTemplateColumnIds: boolean
): KanbanCardData => {
  const primaryStatus = workflowTaskPrimaryStatus(task.status);
  const columnId = useTemplateColumnIds
    ? podCurrentStatusToDisplayString(primaryStatus) ||
      podCurrentStatusToMachineString(primaryStatus) ||
      "Unknown"
    : workflowTaskStatusToColumnId(primaryStatus);

  const statusLine =
    podCurrentStatusToDisplayString(primaryStatus) ||
    podCurrentStatusToMachineString(primaryStatus) ||
    "";

  const nested = taskStatusRecord(task);
  const detailsTail =
    podCurrentStatusToDisplayString(nested?.current_stage) ||
    podCurrentStatusToMachineString(nested?.current_stage) ||
    "";

  const contentText =
    podCurrentStatusToDisplayString(task.current_state) ||
    podCurrentStatusToMachineString(task.current_state) ||
    undefined;

  return {
    id: task._id,
    title: task.name,
    content: contentText,
    columnId,
    assigneeName: task.assignee?.name,
    status: {
      current_status: statusLine,
      details: nested?.call_to_action || detailsTail || "",
    },
    notes: task.notes,
    progress_percentage: taskProgressForCard(task),
    current_state: contentText,
  };
};

// Helper function to generate colors for columns
const generateColumnColor = (index: number): string => {
  const colors = [
    "#A0AEC0", // Light Gray
    "#EF4444", // Red
    "#63B3ED", // Blue
    "#68D391", // Green
    "#F59E0B", // Amber
    "#8B5CF6", // Purple
    "#EC4899", // Pink
    "#10B981", // Emerald
  ];
  return colors[index % colors.length];
};

export function Tasks({ entityId, entityType, fetchTasks, podTemplate }: TasksProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("kanban");
  const { lastMessage, subscribe, unsubscribe } = useWebSocket();

      // Generate dynamic Kanban columns from pod template
  const generateKanbanColumns = (): KanbanColumnData[] => {
    if (!podTemplate?.workflow_config?.status_values) {
      // Fallback to default columns if no template
      return [
        {
          id: "todo",
          title: "To Do",
          color: "#A0AEC0",
          cards: [],
        },
        {
          id: "blocked",
          title: "Blocked",
          color: "#EF4444",
          cards: [],
        },
        {
          id: "inprogress",
          title: "In Progress",
          color: "#63B3ED",
          cards: [],
        },
        {
          id: "done",
          title: "Done",
          color: "#68D391",
          cards: [],
        },
      ];
    }

    return podTemplate.workflow_config.status_values.map((status: string | { label: string; instructions?: string }, index: number) => {
      const label = typeof status === 'string' ? status : status.label;
      return {
        id: label,
        title: label,
        color: generateColumnColor(index),
        cards: [],
      };
    });
  };

  const [kanbanColumns, setKanbanColumns] = useState<KanbanColumnData[]>([]);

  // Update columns when podTemplate changes
  useEffect(() => {
    setKanbanColumns(generateKanbanColumns());
  }, [podTemplate]);

  // Fetch initial tasks
  useEffect(() => {
    async function loadTasks() {
      try {
        setLoading(true);
        const data = await fetchTasks(entityId);
        setTasks(data);
        // Kanban columns will be updated by the tasks useEffect
      } catch (err) {
        console.error("Failed to fetch tasks:", err);
        setError(err instanceof Error ? err.message : "Failed to load tasks");
      } finally {
        setLoading(false);
      }
    }

    loadTasks();
  }, [entityId, fetchTasks]);

    // Effect to update Kanban columns when tasks change
  useEffect(() => {
    if (tasks.length === 0 && !loading) {
      // If tasks are cleared or empty after load
      const clearedKanbanColumns = generateKanbanColumns().map((col) => ({
        ...col,
        cards: [],
      }));
      setKanbanColumns(clearedKanbanColumns);
      return;
    }

    const useTemplateColumnIds = Boolean(
      podTemplate?.workflow_config?.status_values?.length
    );
    const taskCards: KanbanCardData[] = tasks.map((task) =>
      mapTaskToKanbanCard(task, useTemplateColumnIds)
    );
    const newKanbanColumns = generateKanbanColumns().map((column) => ({
      ...column,
      cards: taskCards.filter((card) => card.columnId === column.id),
    }));
    setKanbanColumns(newKanbanColumns);
  }, [tasks, loading, podTemplate]); // podTemplate affects column ids (template labels vs fixed todo/done)

  // WebSocket Subscription and Message Handling
  useEffect(() => {
    const channel = WS_CHANNELS.TASKS;
    subscribe(channel);

    return () => {
      unsubscribe(channel);
    };
  }, [subscribe, unsubscribe]);

  useEffect(() => {
    const message: any = lastMessage.get(WS_CHANNELS.TASKS);

    if (message && message.full_document) {
      const taskData = message.full_document as Task;

      // Check if the task belongs to the current entityId
      if (taskData.pod_instance_id !== entityId) {
        return;
      }

      if (message.operation === "insert") {
        setTasks((prevTasks) => {
          // Avoid duplicates
          if (prevTasks.find((task) => task._id === taskData._id)) {
            return prevTasks;
          }
          return [...prevTasks, taskData];
        });
      } else if (message.operation === "update") {
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task._id === taskData._id ? { ...task, ...taskData } : task
          )
        );
      }
    }
  }, [lastMessage, entityId]);

  const handleCardMove = (
    cardId: string,
    sourceColumnId: string,
    destinationColumnId: string,
    newIndex: number
  ) => {
    // Backend update logic would go here
  };

  if (loading) {
    return <div className="p-4 flex h-full w-full text-slate-600 dark:text-slate-400">Loading tasks...</div>;
  }

  if (error) {
    return (
      <div className="p-4 flex h-full w-full text-red-500">Error: {error}</div>
    );
  }

  const viewToggleOptions: ButtonGroupOption<ViewMode>[] = [
    {
      value: "list",
      label: (
        <span className="flex items-center">
          <FontAwesomeIcon icon={faList} />
        </span>
      ),
    },
    {
      value: "kanban",
      label: (
        <span className="flex items-center">
          <FontAwesomeIcon icon={faChartKanban} />
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 flex flex-col h-full w-full flex-grow">
      {/* <div className="mb-4 flex justify-end">
        <ButtonGroup
          options={viewToggleOptions}
          value={viewMode}
          onChange={(newMode) => setViewMode(newMode as ViewMode)}
          size="lg"
          fullWidth={false}
        />
      </div> */}

      {viewMode === "list" ? (
        <div className="space-y-4 w-full">
          {tasks.map((task) => {
            const primary = workflowTaskPrimaryStatus(task.status);
            const taskStatusNorm =
              podCurrentStatusToMachineString(primary) ||
              podCurrentStatusToDisplayString(primary) ||
              "";
            const stLower = taskStatusNorm.toLowerCase();
            return (
            <div key={task._id} className="rounded-lg p-4 px-2.5 py-0 ">
              <div className="flex items-start gap-6">
                <div
                  className={`rounded-full p-1 size-6 flex items-center justify-center ${
                    stLower === "completed" ||
                    stLower === "done"
                      ? "bg-green-400"
                      : "bg-gray-transparent ring-2 ring-inset ring-slate-200 dark:ring-slate-700"
                  }`}
                >
                  {(stLower === "completed" ||
                    stLower === "done") && (
                    <FontAwesomeIcon icon={faCheck} className="text-white" />
                  )}
                  {stLower !== "completed" &&
                    stLower !== "done" && (
                      <FontAwesomeIcon
                        icon={faCircle}
                        className="text-transparent"
                      />
                    )}
                </div>
                <div className="flex-1">
                  <h3
                    className={`font-medium ${
                      stLower === "completed" ||
                      stLower === "done"
                        ? "text-gray-600 dark:text-gray-300 line-through"
                        : ""
                    }`}
                  >
                    {task.name}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                    {task.description}
                  </p>
                  <div className="text-xs text-gray-400 dark:text-gray-400 mt-2 font-mono">
                    Created {new Date(task.created_at).toLocaleDateString()}
                    {task.assignee && (
                      <span className="ml-2">
                        | Assignee: {task.assignee.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <KanbanBoard
          initialColumns={kanbanColumns}
          onCardMove={handleCardMove}
          variant={entityType === "instance" ? "compact" : "default"}
        />
      )}
    </div>
  );
}
